/**
 * The acceptance gate's state machine (ADR-0497) — a pure function, so the one
 * decision that can blank the entire app is unit-testable without a router, a
 * provider tree, or a mocked Apollo client.
 *
 * The states are deliberately five rather than a boolean, because a boolean has
 * to lie about two of them. "Not yet known" is not "not accepted", and "we could
 * not find out" is neither — collapsing either into `false` would put a signed
 * user behind an acceptance screen during a network blip, and collapsing them
 * into `true` would let the screen flash past before the answer arrived.
 */

export type TermsGateStatus =
  /**
   * The gate does not apply: nobody is signed in, or Auth0 has not resolved the
   * session yet. Renders children untouched, so public routes and the sign-in
   * page are never affected and `AuthGuard` keeps owning the auth loading frame.
   */
  | { readonly kind: 'not-applicable' }
  /**
   * Signed in; the answer is in flight. The ONE state that must render a
   * placeholder, and it renders the same boot skeleton the workspace hydrator
   * uses so the two blocking phases are pixel-identical.
   */
  | { readonly kind: 'unknown' }
  | { readonly kind: 'accepted' }
  /** Signed in and not accepted. The interstitial renders. */
  | {
      readonly kind: 'required';
      readonly currentVersion: string;
      /**
       * The version this person previously accepted, if any. Drives "we have
       * updated our terms" instead of a first-time ask — a returning customer
       * greeted as though they had never agreed to anything reads as a bug.
       */
      readonly previouslyAcceptedVersion: string | null;
    }
  /**
   * Settled with no usable answer. Renders children — the client gate FAILS OPEN
   * on purpose.
   *
   * This is the same posture the billing access banner takes ("A failed read
   * renders nothing. The banner is advisory; the gate is server-side"), and the
   * reasoning is that the two gates have different jobs. Blanking the whole
   * product because one query failed would make a transient database blip
   * indistinguishable from an outage, for a user who may well have accepted
   * already. Enforcement is the server guard, which refuses on a definite
   * negative; this one exists so the user is ASKED rather than stonewalled.
   */
  | { readonly kind: 'unavailable' };

/** The shape the Apollo query hands back, narrowed to what the decision needs. */
export interface TermsAcceptanceSnapshot {
  readonly accepted: boolean;
  readonly currentVersion: string;
  readonly acceptedVersion?: string | null;
}

export interface TermsGateInput {
  /** Auth0's `isLoading`. */
  readonly authLoading: boolean;
  readonly isAuthenticated: boolean;
  /** The query's data, if it has produced any. */
  readonly data: TermsAcceptanceSnapshot | undefined;
  /** True once the query has settled with an error and no usable data. */
  readonly failed: boolean;
  /** The signed-in user's `sub`, when there is one. */
  readonly userSub: string | undefined;
  /**
   * The sub that accepted during THIS page session, set the instant an acceptance
   * succeeds. Belt to the cache write's braces: the provider also writes the
   * mutation result into the query's cache entry, but a cache miss there would put
   * a loading frame between the click and the dashboard, and both mechanisms point
   * the same way.
   *
   * It stores the SUB rather than a boolean, and that is not paranoia about a
   * hypothetical. A boolean is a latch on the React tree, not on the person, so it
   * would keep reporting "accepted" if the tree ever outlived a user switch. Auth0's
   * redirect flows all full-page-navigate, so today it cannot — but "cannot today"
   * is the wrong thing for a flag that opens the product to rest on, and comparing
   * subs costs nothing.
   */
  readonly acceptedSub: string | null;
}

export function resolveTermsGateStatus(input: TermsGateInput): TermsGateStatus {
  // Auth first: while the session is resolving nobody is signed in yet, and
  // AuthGuard is already rendering the branded loading state. Claiming `unknown`
  // here would replace it with a second, different placeholder.
  if (input.authLoading || !input.isAuthenticated) return { kind: 'not-applicable' };

  // Only for the person who accepted — never a latch that survives a user change.
  if (input.acceptedSub !== null && input.acceptedSub === input.userSub) {
    return { kind: 'accepted' };
  }

  if (input.data !== undefined) {
    if (input.data.accepted) return { kind: 'accepted' };
    return {
      kind: 'required',
      currentVersion: input.data.currentVersion,
      previouslyAcceptedVersion: input.data.acceptedVersion ?? null,
    };
  }

  // Data beats failure: under `errorPolicy: 'all'` a partial error can arrive
  // alongside a usable result, and an answer we have is better than a refusal to
  // decide.
  if (input.failed) return { kind: 'unavailable' };

  return { kind: 'unknown' };
}
